import { useEffect, useState, useRef } from "react";
import { Gift, Copy, Check, Users, Sparkles, Mail, QrCode as QrIcon, Download } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function Referrals() {
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showQr, setShowQr] = useState(false);
  const qrRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/referrals/me");
        setStats(data);
      } catch {
        toast.error("Could not load referral stats");
      } finally { setLoading(false); }
    })();
  }, []);

  // Vanity `/join/{code}` is the shareable one — cleaner than `/login?ref=`
  // and matches every promo asset. Backend accepts either.
  const shareUrl = stats ? `${window.location.origin}/join/${stats.referral_code}` : "";

  const copy = async (text, label = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(label);
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copy failed — select and copy manually"); }
  };

  const shareEmail = () => {
    const subj = encodeURIComponent("Try iKEY — the CRM that tells you who to call");
    const body = encodeURIComponent(
      `I've been using iKEY to run my day and it's saving me hours. ` +
      `Sign up with my link and we both get a leg up:\n\n${shareUrl}\n\n— Sent from iKEY`
    );
    // Open the mail composer in a new tab so the referral page stays put.
    // Some browsers ignore _blank on mailto: — fall back to a same-window handoff
    // via a temporary anchor click so the email client still opens.
    const url = `mailto:?subject=${subj}&body=${body}`;
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (!win) {
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a); a.click(); a.remove();
    }
  };

  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `ikey-referral-${stats?.referral_code || "share"}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (!stats) return null;

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold tracking-tighter text-slate-900" data-testid="referrals-title">Referrals</h1>
        <p className="mt-1 text-sm text-slate-500">Invite an agent. When they start a paid subscription, you get one free month.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <StatCard icon={<Users className="h-5 w-5" />} label="Invited" value={stats.referred_count} testid="stat-invited" />
        <StatCard icon={<Sparkles className="h-5 w-5" />} label="Subscribed" value={stats.subscribed_count} testid="stat-subscribed" />
        <StatCard icon={<Gift className="h-5 w-5" />} label="Free months earned" value={stats.free_months_credit} testid="stat-credit" />
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-6 mb-6">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-md text-white" style={{ background: "var(--primary-hex)" }}>
            <Gift className="h-4 w-4" />
          </span>
          <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">Your share link</h2>
        </div>
        <p className="mt-2 text-sm text-slate-500">Send this anywhere — email, text, DM. The invited agent lands on a pre-filled sign-up form.</p>

        <div className="mt-5 flex items-center gap-2">
          <div className="flex-1 rounded-md border border-slate-300 bg-slate-50 px-3 py-2.5 font-mono text-sm text-slate-800 truncate" data-testid="share-url">
            {shareUrl}
          </div>
          <button
            onClick={() => copy(shareUrl, "Share link copied")}
            className="inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold text-white"
            style={{ background: "var(--primary-hex)" }}
            data-testid="copy-share-url-btn"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>

        <div className="mt-3 flex items-center gap-3 text-xs">
          <button onClick={shareEmail} className="inline-flex items-center gap-1.5 text-slate-600 hover:text-slate-900" data-testid="share-email-btn">
            <Mail className="h-3.5 w-3.5" /> Share via email
          </button>
          <span className="text-slate-300">·</span>
          <button onClick={() => setShowQr(v => !v)} className="inline-flex items-center gap-1.5 text-slate-600 hover:text-slate-900" data-testid="toggle-qr-btn">
            <QrIcon className="h-3.5 w-3.5" /> {showQr ? "Hide QR" : "Show QR"}
          </button>
          <span className="text-slate-300">·</span>
          <span className="text-slate-500">Code: <span className="font-mono font-semibold text-slate-800" data-testid="referral-code">{stats.referral_code}</span></span>
        </div>

        {showQr && (
          <div className="mt-5 rounded-md border border-slate-200 bg-slate-50 p-5 flex items-center gap-6" data-testid="qr-card">
            <div ref={qrRef} className="rounded bg-white p-3 shadow-sm">
              <QRCodeCanvas value={shareUrl} size={160} level="M" includeMargin={false} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Scan to sign up</div>
              <p className="mt-2 text-sm text-slate-700 leading-snug">
                At open houses, hand your phone to an agent — they scan and land on a pre-filled signup form. Your referral code is baked in.
              </p>
              <button onClick={downloadQr} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 px-3 py-1.5 rounded" data-testid="download-qr-btn">
                <Download className="h-3.5 w-3.5" /> Download PNG
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-lg bg-slate-900 text-white p-6">
        <h3 className="font-heading text-sm uppercase tracking-widest text-slate-400">How it works</h3>
        <ol className="mt-4 space-y-3 text-sm text-slate-200">
          <li className="flex gap-3"><span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/10 text-xs font-semibold flex-shrink-0">1</span>Send your link to an agent who could use a better CRM.</li>
          <li className="flex gap-3"><span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/10 text-xs font-semibold flex-shrink-0">2</span>They sign up — your code auto-attaches from the URL.</li>
          <li className="flex gap-3"><span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/10 text-xs font-semibold flex-shrink-0">3</span>When they start a paid subscription, you earn one free month.</li>
        </ol>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-500">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-slate-100">{icon}</span>
        <span className="text-xs font-medium uppercase tracking-widest">{label}</span>
      </div>
      <div className="mt-4 font-heading text-4xl font-bold tabular-nums tracking-tighter text-slate-900">{value}</div>
    </div>
  );
}
